import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addCategory, uploadCategoryImage } from '../api/categoryApi';
import { resolveImageUrl } from '../utils/image';
import AdminNav from './AdminNav';
import './AdminDashboard.css';

export default function AddCategoryPage() {
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [displayOrder, setDisplayOrder] = useState<string>('');
  const [imageUrl, setImageUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      setFile(selected);
      setPreview(URL.createObjectURL(selected));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      let finalPath = imageUrl;
      if (file) {
        const uploaded = await uploadCategoryImage(file);
        finalPath = uploaded.path;
      }
      const result = await addCategory({ 
        CategoryName: categoryName, 
        CategoryDescription: categoryDescription, 
        ImageUrl: finalPath,
        DisplayOrder: displayOrder ? parseInt(displayOrder, 10) : undefined
      });
      if (result > 0) {
        navigate('/admin/categories');
      } else {
        setError('Failed to create category. Please check your inputs.');
      }
    } catch {
      setError('Server error while saving category. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ad-page-wrapper">
      <AdminNav activeTab="add-category" />

      <main className="ad-main-content">
        <div className="ad-form-page-container">
          {/* Header */}
          <div className="ad-form-header">
            <button className="ad-back-btn" onClick={() => navigate('/admin/categories')}>
              &larr; Back to Categories
            </button>
            <div className="ad-form-title-group">
              <span className="ad-modal-tag">CATALOG MANAGEMENT</span>
              <h1 className="ad-form-main-title">Create New Category</h1>
              <p className="ad-form-subtitle">Add a new category to group related fireworks products</p>
            </div>
          </div>

          {/* Form Card */}
          <div className="ad-form-card">
            <form onSubmit={handleSubmit} className="ad-crud-form">
              <div className="ad-form-field">
                <label>Category Name <span className="req">*</span></label>
                <input
                  type="text"
                  value={categoryName}
                  onChange={e => setCategoryName(e.target.value)}
                  required
                  className="ad-input"
                  placeholder="e.g. Ground Chakkars, Sparklers, Sky Shots..."
                />
              </div>

              <div className="ad-form-field">
                <label>Description</label>
                <textarea
                  value={categoryDescription}
                  onChange={e => setCategoryDescription(e.target.value)}
                  className="ad-input textarea"
                  placeholder="Brief description explaining this fireworks category..."
                  rows={3}
                />
              </div>

              <div className="ad-form-field">
                <label>Display Order / Sequence Rank</label>
                <input
                  type="number"
                  min="1"
                  value={displayOrder}
                  onChange={e => setDisplayOrder(e.target.value)}
                  className="ad-input"
                  placeholder="e.g. 1 (1st), 2 (2nd)... leave empty to place last"
                />
                <span className="ad-input-hint">Items with rank 1 appear first for customers on the website.</span>
              </div>

              <div className="ad-form-field">
                <label>Category Thumbnail Image</label>
                
                {preview && (
                  <div className="ad-image-preview-box">
                    <img src={preview} alt="Selected preview" />
                    <button 
                      type="button" 
                      className="ad-remove-preview-btn" 
                      onClick={() => { setFile(null); setPreview(''); setImageUrl(''); }}
                      title="Remove image"
                    >
                      ✕
                    </button>
                  </div>
                )}

                <div className="ad-upload-input-wrap">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="ad-file-input"
                  />
                </div>

                <div className="ad-or-divider">OR ENTER EXTERNAL IMAGE URL</div>

                <input
                  type="text"
                  value={imageUrl}
                  onChange={e => {
                    setImageUrl(e.target.value);
                    setPreview(e.target.value ? resolveImageUrl(e.target.value) : '');
                  }}
                  className="ad-input"
                  placeholder="https://example.com/fireworks-category.png"
                />
              </div>

              {error && <div className="ad-error-alert">{error}</div>}

              <div className="ad-form-actions-row">
                <button 
                  className="ad-btn-action complete" 
                  type="submit" 
                  disabled={loading}
                >
                  {loading ? 'Creating Category...' : 'Save & Publish Category'}
                </button>
                <button 
                  className="ad-btn-action close" 
                  type="button" 
                  onClick={() => navigate('/admin/categories')}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}